<script setup lang="ts">
import { MContainer, MContainerGrow, RgTitle, MUButton, MContainerRow } from "@xlabmedical/medic-web-framework";
import DatePicker from "primevue/datepicker";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { useI18n } from "vue-i18n";
import { PatientListModel } from "@/models/PatientListModel";

const { t, locale } = useI18n();

const props = withDefaults(
    defineProps<{
        showMultipleSelection?: boolean;
        showSearch?: boolean;
    }>(),
    {
        showMultipleSelection: true,
        showSearch: true,
    },
);

const searchString = defineModel<string>("searchString", { default: "" });
const selectMultiple = defineModel<boolean>("selectMultiple");
const filterDateRange = defineModel<[Date | null, Date | null] | null>("filterDateRange");
const selectedPatients = defineModel<PatientListModel[]>("selectedPatients", { default: () => [] });

const emits = defineEmits(["addPatient", "toggleFilter", "deleteClicked", "exportClicked", "filtersCleared"]);

const menu = ref();
const menuFilter = ref();
const selectMultipleBtn = ref();
const actionsBtn = ref();
const filterBtn = ref();
const filterMenuTarget = ref<HTMLElement | null>(null);

const hasSelection = computed(() => (selectedPatients.value?.length ?? 0) > 0);
const selectedPatientsCount = computed(() => selectedPatients.value?.length ?? 0);
const selectedPatientsCountBadge = computed(() => (selectedPatientsCount.value > 99 ? "99+" : `${selectedPatientsCount.value}`));
const isEnglishAppLanguage = computed(() => String(locale.value).toLowerCase().startsWith("en"));

const items = computed(() => [
    {
        items: [
            {
                label: t("shared.export"),
                icon: "pi pi-upload !text-white",
                command: () => emits("exportClicked"),
                disabled: !hasSelection.value,
            },
            {
                label: t("shared.delete"),
                icon: "pi pi-trash !text-white",
                command: () => emits("deleteClicked"),
                disabled: !hasSelection.value,
            },
        ],
    },
]);

const clearFilters = () => {
    filterDateRange.value = null;
    emits("filtersCleared");
};

const clearFiltersKeepOpen = (event?: Event) => {
    event?.preventDefault();
    event?.stopPropagation();
    clearFilters();

    nextTick(() => {
        const target = filterMenuTarget.value;
        if (!target) return;
        menuFilter.value?.show?.({ currentTarget: target, target });
    });
};

const itemsFilter = ref([
    {
        items: [
            {
                label: "DatePicker",
                type: "datepicker",
            },
            {
                label: t("web-rg.removeFilters"),
                icon: "pi pi-times",
                command: () => clearFilters(),
            },
        ],
    },
]);

const isFilterActive = computed(() => Boolean(filterDateRange.value?.some((date) => date)));

const toggle = (event?: Event) => {
    const eventTarget = (event?.currentTarget ?? event?.target) as EventTarget | null;
    const fallbackTarget = actionsBtn.value?.$el ?? actionsBtn.value;
    const target = eventTarget instanceof HTMLElement ? eventTarget : fallbackTarget instanceof HTMLElement ? fallbackTarget : null;
    if (!target) {
        return;
    }
    menu.value?.toggle?.({ currentTarget: target, target });
};

const toggleMultipleSelection = () => {
    selectMultiple.value = !selectMultiple.value;
};

const showSelectMenu = () => {
    const target = selectMultipleBtn.value?.$el ?? selectMultipleBtn.value;
    if (!target) {
        return;
    }
    menu.value?.show?.({ currentTarget: target, target });
};

const toggleFilter = (event?: Event) => {
    const eventTarget = (event?.currentTarget ?? event?.target) as EventTarget | null;
    const fallbackTarget = filterBtn.value?.$el ?? filterBtn.value;
    const target = eventTarget instanceof HTMLElement ? eventTarget : fallbackTarget instanceof HTMLElement ? fallbackTarget : null;
    if (!target) {
        return;
    }
    filterMenuTarget.value = target;
    menuFilter.value?.toggle?.({ currentTarget: target, target });
};

watch(
    () => selectMultiple.value,
    (value) => {
        selectedPatients.value = [];
        if (value) {
            nextTick(() => showSelectMenu());
        } else {
            menu.value?.hide?.();
        }
    },
);
</script>

<template>
    <MContainerRow class="align-center w-full horizontal-padding gap-4" style="border-bottom: 1px var(--p-rg-border-color) solid; height: 6.36rem !important">
        <RgTitle>{{ $t("shared.patients") }}</RgTitle>
        <MUButton
            class="rg-primary-color p-button-rounded"
            id="btnAddPatient"
            :alt="$t('web-rg.addNewPatient')"
            :iconSrc="'/icons/btn_icon_plus.svg'"
            @click="emits('addPatient')"
            :disabled="selectMultiple"
            style="padding: 0 !important"
            data-cy="addPatientButton" />
        <MContainerGrow />
        <MContainer class="gap-2">
            <MContainer v-if="selectMultiple && selectedPatientsCount > 0">
                <MUButton ref="actionsBtn" class="web-rg-pl-icon" aria-haspopup="true" aria-controls="overlay_menu" icon="pi pi-ellipsis-v" @click="toggle" />
                <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" :key="hasSelection ? 'selection' : 'none'">
                    <template #start>
                        <div v-if="!hasSelection" class="rg-select-patient-hint">{{ $t("shared.select") }} {{ $t("shared.patient") }}{{ isEnglishAppLanguage ? "(s)" : "" }}</div>
                    </template>
                </Menu>
            </MContainer>
            <MContainer v-if="props.showMultipleSelection" class="rg-multiple-select-button-wrap">
                <MUButton
                    ref="selectMultipleBtn"
                    :title="$t('web-rg.multipleSelection')"
                    :iconSrc="'/icons/rg_multiple_select.svg'"
                    :iconSrcHeight="'22px'"
                    :iconSrcWidth="'22px'"
                    class="web-rg-pl-icon"
                    :class="[selectMultiple ? 'rg-hover-active' : '']"
                    @click="toggleMultipleSelection" />
                <span v-if="selectMultiple && selectedPatientsCount > 0" class="rg-selected-patients-badge">
                    {{ selectedPatientsCountBadge }}
                </span>
            </MContainer>
            <MContainer>
                <MUButton
                    ref="filterBtn"
                    icon="pi pi-filter"
                    class="web-rg-pl-icon"
                    :class="[selectMultiple ? 'opacity-50 pointer-events-none' : '', isFilterActive ? 'rg-filter-active' : '']"
                    aria-controls="overlay_menu_filter"
                    @click="toggleFilter" />
                <Menu ref="menuFilter" id="overlay_menu_filter" :model="itemsFilter" :popup="true">
                    <template #item="{ item }">
                        <div v-if="item.type === 'datepicker'" class="rg-filter-calendar">
                            <DatePicker v-model="filterDateRange" selectionMode="range" :manualInput="false" inline />
                        </div>
                        <a
                            v-else-if="item.label === $t('web-rg.removeFilters')"
                            class="p-menuitem-link gap-2 flex items-center pl-4 m-u-button m-u-button-outlined m-u-button-background m-u-no-button-toolbar"
                            role="menuitem"
                            tabindex="0"
                            @click="clearFiltersKeepOpen">
                            <span v-if="item.icon" :class="['p-menuitem-icon', item.icon]" />
                            <span class="p-menuitem-text">{{ item.label }}</span>
                        </a>
                    </template>
                </Menu>
            </MContainer>
            <MContainer v-if="props.showSearch">
                <IconField iconPosition="right" class="flex flex-row flex-wrap mb-1 grow">
                    <InputIcon class="pi pi-search" />
                    <InputText v-model="searchString" class="grow" v-bind:placeholder="$t('shared.search')" :disabled="selectMultiple" />
                </IconField>
            </MContainer>
        </MContainer>
    </MContainerRow>
</template>

<style scoped>
.rg-filter-calendar {
    padding: 8px 12px;
}

:deep(.p-datepicker-panel) {
    background: transparent;
}

:deep(.p-datepicker-header) {
    background: transparent;
}

:deep(.p-datepicker td span:hover) {
    background: transparent !important;
    border-color: transparent !important;
}

:deep(.p-datepicker-panel) {
    width: auto;
    padding: 0;
    background: transparent !important;
    border: 0;
}

:deep(.rg-filter-active) {
    background: var(--p-button-primary-background) !important;
}

:deep(.rg-hover-active) {
    background: var(--p-primary-hover-color) !important;
}

:deep(.rg-has-selection .pi) {
    color: white !important;
}

.rg-select-patient-hint {
    font-size: 0.8rem;
    opacity: 0.7;
    padding: 0.5rem 0.75rem 0.25rem 0.75rem;
}

.rg-multiple-select-button-wrap {
    position: relative;
    display: inline-flex;
}

.rg-selected-patients-badge {
    position: absolute;
    bottom: 2px;
    right: -4px;
    min-width: 14px;
    height: 14px;
    padding: 0;
    border-radius: 999px;
    background: #ffffff;
    color: #000000;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.rg-filter-btn {
    color: var(--p-inputtext-placeholder-color, #8a93a3);
    border-color: var(--p-inputtext-placeholder-color, #8a93a3);
}
</style>
